'use client';

import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/nextjs';
import { createClient } from '@/lib/supabase/client';
import {
  classifyRefreshError,
  shouldRefreshSession,
} from '@/lib/auth/session-refresh-policy';
import { trackAuthSession } from '@/lib/analytics/auth-session';

const STABLE_SINCE_KEY = '__sessionStableSince';
const STABLE_HEAL_GRACE_MS = 5 * 60 * 1000;

function loginUrlWithReturnTo(): string {
  if (typeof window === 'undefined') return '/login';
  const path = window.location.pathname + window.location.search;
  // Avoid bouncing /login → /login?returnTo=/login. Matches both bare
  // /login and the locale-prefixed /<locale>/login variants.
  if (/(^|\/)login(\?|\/|#|$)/.test(path)) return '/login';
  return `/login?returnTo=${encodeURIComponent(path)}`;
}

export function useSessionKeepAlive() {
  const inFlightRef = useRef<Promise<void> | null>(null);

  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    const refreshIfNeeded = async () => {
      if (inFlightRef.current) return inFlightRef.current;

      inFlightRef.current = (async () => {
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          const needsRefresh = shouldRefreshSession(
            sessionData.session,
            Date.now(),
          );

          // Recovery flag self-heal is gated by a "session has been stable
          // for at least 5 minutes" check. The previous behavior cleared
          // flags as soon as we observed a comfortably valid session,
          // which would silently re-arm the auto-reload budget on every
          // visibility tick — under flaky network that can disguise a
          // slow reload-loop as flakiness. Now we record the first stable
          // observation, and only clear flags after the grace window.
          if (typeof window !== 'undefined') {
            if (sessionData.session && !needsRefresh) {
              const stableSinceRaw = sessionStorage.getItem(STABLE_SINCE_KEY);
              if (!stableSinceRaw) {
                sessionStorage.setItem(STABLE_SINCE_KEY, String(Date.now()));
              } else {
                const elapsed = Date.now() - Number(stableSinceRaw);
                if (elapsed >= STABLE_HEAL_GRACE_MS) {
                  sessionStorage.removeItem('__authReloadAttempt');
                  sessionStorage.removeItem('__sessionRedirected');
                }
              }
            } else {
              // Either no session or fragile (inside refresh buffer) —
              // reset the stability clock so a future stable window has
              // to be earned again before flags clear.
              sessionStorage.removeItem(STABLE_SINCE_KEY);
            }
          }

          if (!needsRefresh) return;

          const { error } = await supabase.auth.refreshSession();
          if (error && !cancelled) {
            const classification = classifyRefreshError(
              (error as { message?: string }).message,
            );
            if (
              classification === 'fatal' &&
              typeof window !== 'undefined' &&
              !sessionStorage.getItem('__sessionRedirected')
            ) {
              sessionStorage.setItem('__sessionRedirected', '1');
              window.location.assign(loginUrlWithReturnTo());
            }
          }
        } finally {
          inFlightRef.current = null;
        }
      })();

      return inFlightRef.current;
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void refreshIfNeeded();
      }
    };

    const handleFocus = () => {
      void refreshIfNeeded();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void refreshIfNeeded();
      }
    };

    // Note: we intentionally do NOT refresh on the `online` event by itself.
    // A network blip (WiFi ↔ cellular) flipping `navigator.onLine` would
    // otherwise trigger a refreshSession() that competes with the same
    // recovery storm — and any transient classify-as-retryable error
    // would still surface as a churn we can avoid. Visibility / focus
    // ticks are sufficient to keep the token fresh once the user comes
    // back to the tab.

    // Mount-time check so stale recovery flags get cleared and tokens
    // already past the buffer get refreshed without waiting for the next
    // visibility / focus event (relevant after auto-reload paths).
    void refreshIfNeeded();

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);

    // Surface explicit auth state transitions so we don't sit on a stale UI
    // when supabase-js itself has already invalidated the session (another
    // tab signed out, refresh token rotated and got rejected, etc).
    //
    // We only react to SIGNED_OUT today. If a future supabase-js version
    // emits a more specific fatal-refresh signal (e.g. TOKEN_REFRESH_FAILED),
    // add it to this check — the in-loop refreshSession() error path
    // already covers fatal refresh errors discovered through that route.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;

      // 가입 유입 귀속용 auth_session (브라우저 세션·사용자당 1회, 내부에서 중복 차단).
      // 별도 구독을 두지 않고 이 전역 리스너에 얹는다.
      trackAuthSession(event, session);

      // Sentry 유저 컨텍스트. 이 구독이 앱 전체에서 유일한 전역 auth 리스너라
      // 여기서만 붙이면 모든 클라이언트 이벤트가 사용자 id를 갖는다. 이게 없어서
      // 그동안 얼굴 인증 에러가 전부 "Users: 0"으로 잡혀, 문의한 고객의 이벤트를
      // 역추적할 수 없었다. id만 넣는다 — sendDefaultPii:false 기조에 맞춰
      // 이메일 등은 절대 싣지 않는다. 구독 시 INITIAL_SESSION이 곧바로 오므로
      // 이미 로그인된 사용자도 추가 조회 없이 커버된다(auth lock 경합 회피).
      if (session?.user?.id) {
        Sentry.setUser({ id: session.user.id });
      } else if (event === 'SIGNED_OUT') {
        Sentry.setUser(null);
      }

      if (event === 'SIGNED_OUT') {
        if (
          typeof window !== 'undefined' &&
          !sessionStorage.getItem('__sessionRedirected')
        ) {
          sessionStorage.setItem('__sessionRedirected', '1');
          window.location.assign(loginUrlWithReturnTo());
        }
      }
    });

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
      subscription.unsubscribe();
    };
  }, []);
}
